import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { first } from 'rxjs/operators';

import CCservices from '@C/CreditCustomers/_utils/data';
import Utilservices from '@C/Utils/data';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { AuthRoute, Role } from '@H';
import {
  Banking,
  BunkSetup,
  CashiersOnDuty,
  CreditCustomers,
  Dashboard,
  DataBackUp,
  DipReading,
  Dsr,
  LiveMemo,
  Loans,
  Login,
  Lubes,
  MyAccountPage,
  OtherIE,
  PrivacyPolicy,
  Purchases,
  ReferralProgram,
  Reports,
  ServerMaintenance,
  Settings,
  StaffManagement,
  TallyExport,
  UnauthorizedPage,
} from '@P';
import {
  CreateDealerPage,
  DipCalibrationPage,
  GeneratePurchaseOrderPage,
  SmsSetup,
  UsersPage,
  Packages,
} from '@P/Admin';
import {
  CreditLedgerPage,
  CreditSalePage,
  InvoicePage,
  MyProfile,
  PaymentsPage,
  PurchaseOrderPage,
  QuotationPage,
  RemindersPage,
} from '@P/CreditCustomerProfile';
import { authService } from '@S';
import { useGlobalStore } from '@stores/globalStore';

import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

const Logout = () => {
  authService.logout();
  window.location = '/login';
  return null;
};

const Core = () => {
  const setCreditCustomers = useGlobalStore(
    (state) => state.setCreditCustomers,
  );

  const setProductList = useGlobalStore((state) => state.setProductList);
  const setTaxList = useGlobalStore((state) => state.setTaxList);
  const setStaffList = useGlobalStore((state) => state.setStaffList);
  const date = dayjs().format('YYYY-MM-DD');
  const [loading, setLoading] = useState(false);
  const [serverMaintenance, setServerMaintenance] = useState(false);
  const fetchNavigationData = useGlobalStore(
    (state) => state.fetchNavigationData,
  );
  const fetchBanners = useGlobalStore((state) => state.fetchBanners);
  const fetchBunkConfig = useGlobalStore((state) => state.fetchBunkConfig);
  const fetchShiftList = useGlobalStore((state) => state.fetchShiftList);
  const fetchStatusRef = useRef({
    creditCustomers: false,
    productList: false,
    taxList: false,
    staffList: false,
  });
  const [bootstrapComplete, setBootstrapComplete] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!authService.currentUserId,
  );

  const hasRedirectedRef = useRef(false);
  const isBootstrappingRef = useRef(false);
  const preExpiryWarningShownRef = useRef(false);

  const fetchData = async (type) => {
    if (isBootstrappingRef.current) return;

    try {
      isBootstrappingRef.current = true;

      const user = await authService.currentUser.pipe(first()).toPromise();
      const role = user.data.user_info.role.code;

      setLoading(true);

      let promises = [fetchBunkConfig()];

      if (role === 100 || type === 'expired') {
        promises = [...promises, fetchNavigationData(), fetchBanners()];
      } else {
        promises = [
          ...promises,
          fetchCreditCustomers(),
          fetchProductList(),
          fetchTaxList(),
          fetchShiftList(),
          fetchNavigationData(),
          fetchBanners(),
        ];
      }

      if (role !== 104 && role !== 100 && type !== 'expired') {
        promises.push(fetchStaffList());
      }

      await Promise.all(promises);
      setBootstrapComplete(true);
    } catch (error) {
      if (error.status !== 401) {
        handleFetchError(error);
      } else {
        console.log(
          'Unauthorized access - likely due to session expiration. Logging out.',
        );
        authService.logout();
        window.location = '/login';
      }
    } finally {
      setLoading(false);
      isBootstrappingRef.current = false;
    }
  };

  const fetchCreditCustomers = async () => {
    if (fetchStatusRef.current.creditCustomers) return true;

    fetchStatusRef.current.creditCustomers = true;

    try {
      const response = await CCservices.getCreditCustomerList(9999999, 1);
      setCreditCustomers(response.data.data);
      return true;
    } catch (error) {
      fetchStatusRef.current.creditCustomers = false; // allow retry
      handleFetchError(error);
      throw error;
    }
  };

  const fetchProductList = async () => {
    if (fetchStatusRef.current.productList) return true;

    fetchStatusRef.current.productList = true;

    try {
      const response = await CCservices.getProductList(date);
      setProductList(response.data.data);
      return true;
    } catch (error) {
      fetchStatusRef.current.productList = false;
      handleFetchError(error);
      throw error;
    }
  };

  const fetchTaxList = async () => {
    if (fetchStatusRef.current.taxList) return true;

    fetchStatusRef.current.taxList = true;

    try {
      const response = await Utilservices.getTaxDetails();
      setTaxList(response.data.data);
      return true;
    } catch (error) {
      fetchStatusRef.current.taxList = false;
      handleFetchError(error);
      throw error;
    }
  };

  const fetchStaffList = async () => {
    if (fetchStatusRef.current.staffList) return true;

    fetchStatusRef.current.staffList = true;

    try {
      // ---- First API: staff lists
      const staffResponse = await Utilservices.getStaffList();
      const data = staffResponse.data.data;

      const cashiers = data.cashiers.map((item) => ({
        value: item.uuid,
        label: `${item.first_name} ${item.last_name}`,
      }));

      const otherStaff = data.other_staff.map((item) => ({
        value: item.uuid,
        label: `${item.first_name} ${item.last_name}`,
      }));

      // ---- Second API: filtered duties
      const dutiesResponse =
        await Utilservices.getFilteredStaffList('2077-01-01');
      const duties = dutiesResponse.data.data.duties_list;

      const dealers = [];
      const managers = [];

      duties.forEach((item) => {
        const entry = {
          value: item.staff_uuid,
          label: `${item.first_name} ${item.last_name}`,
        };

        if (item.role_type === 101) {
          dealers.push(entry);
        } else {
          managers.push(entry);
        }
      });

      // ---- Final options
      const options = [
        { label: 'Cashiers', options: cashiers },
        { label: 'Dealer', options: dealers },
        { label: 'Managers', options: managers },
        { label: 'Other Staff', options: otherStaff },
      ];

      setStaffList(options);
      return true;
    } catch (error) {
      // allow retry if anything fails
      fetchStatusRef.current.staffList = false;
      handleFetchError(error);
      throw error;
    }
  };

  const handleFetchError = (error) => {
    if (error.response === undefined) {
      setServerMaintenance(true);
    }
    console.log(error);
  };

  const shouldRedirectToAccount = (accountId, subscriptionDetails) => {
    return (
      accountId &&
      subscriptionDetails?.is_subscription_expired &&
      window.location.pathname !== '/payment-confirmation' &&
      window.location.pathname !== '/account'
    );
  };

  const shouldFetchData = (bunkEvent, role) => {
    return (bunkEvent && bunkEvent.data?.[0]?.account_id) || role === 100;
  };

  const showPreExpiryWarning = (subscriptionDetails) => {
    if (preExpiryWarningShownRef.current) {
      return;
    }

    const expiryDateValue = subscriptionDetails?.subscription_expiry_date;
    if (!expiryDateValue || subscriptionDetails?.is_subscription_expired) {
      return;
    }

    const expiryDate = dayjs(expiryDateValue);
    if (!expiryDate.isValid()) {
      return;
    }

    const daysRemaining = expiryDate
      .endOf('day')
      .diff(dayjs().startOf('day'), 'day');
    if (daysRemaining >= 0 && daysRemaining <= 5) {
      preExpiryWarningShownRef.current = true;
      toast.warn(`Your plan will expire on ${expiryDate.format('DD/MM/YYYY')}`);
    }
  };

  useEffect(() => {
    const subscription = authService.currentBunk.subscribe((bunkEvent) => {
      const role = authService.currentUserRoleCode;
      const subscriptionDetails = authService.bunkSubscriptionDetails;
      const accountId = authService.currentAccountID;
      if (window.location.pathname === '/logout') {
        setIsAuthenticated(false);
        setBootstrapComplete(true);
        return;
      }
      // ---- Expired subscription redirect (once)
      if (
        !hasRedirectedRef.current &&
        shouldRedirectToAccount(accountId, subscriptionDetails)
      ) {
        hasRedirectedRef.current = true;
        setLoading(false);
        window.location = '/account';
        return; // critical
      }

      showPreExpiryWarning(subscriptionDetails);

      // ---- Data bootstrap
      if (shouldFetchData(bunkEvent, role)) {
        fetchData(
          subscriptionDetails?.is_subscription_expired ? 'expired' : undefined,
        );
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const sub = authService.currentUser.subscribe((user) => {
      if (user) {
        const lTime = authService?.loginTime;
        const currrentDate = Date.now();
        const validity = authService?.currentLoginValidity || 0;
        if (lTime && currrentDate > lTime + validity * 1000) {
          alert('Your session expired. Please login again');
          authService.logout();
          window.location = '/';
        }
      }
      if (!user) {
        // LOGOUT
        setIsAuthenticated(false);
        setBootstrapComplete(false);
        setLoading(false);

        hasRedirectedRef.current = false;
        isBootstrappingRef.current = false;
        preExpiryWarningShownRef.current = false;

        fetchStatusRef.current = {
          creditCustomers: false,
          productList: false,
          taxList: false,
          staffList: false,
        };
      } else {
        // LOGIN
        setIsAuthenticated(true);
      }
    });

    return () => sub.unsubscribe();
  }, []);

  if (serverMaintenance) {
    return <ServerMaintenance />;
  }

  if (!isAuthenticated) {
    return (
      <Router>
        <Switch>
          <Route exact path="/" component={Login} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/logout" component={Logout} />
          <Route exact path="/unauthorized" component={UnauthorizedPage} />
          <Route path="*" exact>
            <Redirect to="/login" />
          </Route>
        </Switch>
      </Router>
    );
  }

  if (!bootstrapComplete) {
    return <DropLoaderLarge />;
  }

  if (bootstrapComplete && isAuthenticated) {
    return (
      <Router>
        <ToastContainer position="bottom-center" autoClose={5000} />
        <Switch>
          <Route exact path="/logout" component={Logout} />
          <Route exact path="/unauthorized" component={UnauthorizedPage} />
          <AuthRoute
            path="/account/banking"
            roles={[Role.Dealer, Role.Manager]}
            component={Banking}
          />
          <AuthRoute
            path="/dashboard"
            roles={[
              Role.Admin,
              Role.Dealer,
              Role.Manager,
              Role.Cashier,
              Role.CreditCustomer,
            ]}
            component={Dashboard}
          />
          <AuthRoute
            path="/cashiers-on-duty"
            roles={[Role.Dealer, Role.Manager, Role.Cashier]}
            component={CashiersOnDuty}
          />
          <AuthRoute
            path="/account/referral-program"
            roles={[Role.Admin, Role.Dealer, Role.Manager]}
            component={ReferralProgram}
          />
          <AuthRoute
            path="/admin/add/dealer"
            roles={Role.Admin}
            component={CreateDealerPage}
          />
          <AuthRoute
            path="/admin/users"
            roles={Role.Admin}
            component={UsersPage}
          />
          <AuthRoute
            path="/subscription"
            roles={Role.Admin}
            component={Packages}
          />
          <AuthRoute
            path="/admin/dip-calibration"
            roles={Role.Admin}
            component={DipCalibrationPage}
          />
          <AuthRoute
            path="/admin/generate-purchase-order"
            roles={Role.Admin}
            component={GeneratePurchaseOrderPage}
          />
          <AuthRoute
            path="/admin/sms-setup"
            roles={Role.Admin}
            component={SmsSetup}
          />
          <AuthRoute
            path="/account/setup/bunk"
            roles={[Role.Dealer, Role.Manager]}
            component={BunkSetup}
          />
          <AuthRoute
            path="/account/other-income-expense"
            roles={[Role.Dealer, Role.Manager]}
            component={OtherIE}
          />
          <AuthRoute
            path="/account/lubes"
            roles={[Role.Dealer, Role.Manager]}
            component={Lubes}
          />
          <AuthRoute
            path="/account/staff-management"
            roles={[Role.Dealer, Role.Manager]}
            component={StaffManagement}
          />
          <AuthRoute
            path="/account/loan"
            roles={[Role.Dealer, Role.Manager]}
            component={Loans}
          />
          <AuthRoute
            path="/account/credit-customers"
            roles={[Role.Dealer, Role.Manager]}
            component={CreditCustomers}
          />
          <AuthRoute
            path="/account/tally-export"
            roles={[Role.Dealer, Role.Manager]}
            component={TallyExport}
          />
          <AuthRoute
            path="/account/data-backup"
            roles={[Role.Dealer, Role.Manager]}
            component={DataBackUp}
          />
          <AuthRoute
            path="/creditcustomer/credit-sale"
            roles={[Role.CreditCustomer]}
            component={CreditSalePage}
          />
          <AuthRoute
            path="/creditcustomer/credit-ledger"
            roles={[Role.CreditCustomer]}
            component={CreditLedgerPage}
          />
          <AuthRoute
            path="/creditcustomer/quotation"
            roles={[Role.CreditCustomer]}
            component={QuotationPage}
          />
          <AuthRoute
            path="/creditcustomer/live-purchase-order"
            roles={[Role.CreditCustomer]}
            component={PurchaseOrderPage}
          />
          <AuthRoute
            path="/creditcustomer/invoice"
            roles={[Role.CreditCustomer]}
            component={InvoicePage}
          />
          <AuthRoute
            path="/creditcustomer/payments"
            roles={[Role.CreditCustomer]}
            component={PaymentsPage}
          />
          <AuthRoute
            path="/creditcustomer/reminders"
            roles={[Role.CreditCustomer]}
            component={RemindersPage}
          />
          <AuthRoute
            path="/account/dip-reading"
            roles={[Role.Dealer, Role.Manager]}
            component={DipReading}
          />
          <AuthRoute
            path="/account/purchases"
            roles={[Role.Dealer, Role.Manager]}
            component={Purchases}
          />
          <AuthRoute
            path="/settings"
            roles={[Role.Dealer, Role.Manager]}
            component={Settings}
          />
          <AuthRoute
            path="/account/live-memo"
            roles={[Role.Dealer, Role.Manager]}
            component={LiveMemo}
          />
          <AuthRoute
            path="/account/dsr/:date"
            roles={[Role.Dealer, Role.Manager]}
            component={Dsr}
          />
          <Route path="/account/dsr/">
            <Redirect to={`/account/dsr/${date}`} />
          </Route>
          <AuthRoute
            path="/account/reports"
            roles={[Role.Dealer, Role.Manager]}
            component={Reports}
          />
          <AuthRoute
            path="/account/creditcustomer"
            roles={[Role.CreditCustomer]}
            component={MyProfile}
          />
          <AuthRoute
            path="/account"
            roles={[Role.Dealer]}
            component={MyAccountPage}
          />

          <Route path="*" exact={true}>
            <Redirect to="/dashboard" />
          </Route>
        </Switch>
      </Router>
    );
  }
};

export default Core;
